/* eslint-disable @typescript-eslint/no-explicit-any */
// Styling
import { Line } from 'react-chartjs-2';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	Filler,
	ScriptableContext,
} from 'chart.js';
import styled, { useTheme } from 'styled-components';
import { Box, Flex } from 'reflexbox';
import moment from 'moment';
import { Skeleton } from '@treams/treams-ui';
import _ from 'lodash';

import * as graphql from 'generated/graphql';
import { useTranslation } from 'react-i18next';

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	Filler,
);

const LineChartBackground = styled(Box)`
	position: relative;

	::after {
		content: '';
		display: block;
		position: absolute;
		left: 20px;
		top: 10px;
		bottom: 20px;
		right: 20px;
		background: ${(props) => props.theme.colors.whiteBlue};
	}
`;

export function LineChartGradient(props: {
	labels: string[];
	data: number[] | { x: number; y: number }[];
	height?: number;
	bounds?: [number, number];
	autoBound?: boolean;
	compactLayout?: boolean;
	minimalLayout?: boolean;
	showBackground?: boolean;
	yFormat?: (x: number) => string;
	tintColor?: string;
}) {
	const theme = useTheme() as any;
	let [min, max] = props.bounds ?? [undefined, undefined];

	if (props.autoBound && props.data.length > 0) {
		const ys =
			typeof props.data[0] === 'number'
				? (props.data as number[])
				: (props.data as { y: number }[]).map((p) => p.y);
		const [absMin, absMax] = [_.min(ys) ?? 0, _.max(ys) ?? 0];
		const minMargin = 1e-3;
		const rangeOrder = Math.round(Math.log10(absMax - absMin + minMargin * 2));
		const maxOffset = Math.pow(10, rangeOrder);

		min = Math.floor((absMin - minMargin) / maxOffset) * maxOffset;
		max = Math.ceil((absMax + minMargin) / maxOffset) * maxOffset;

		if (props.bounds) {
			min = Math.max(min, props.bounds[0]);
			max = Math.min(max, props.bounds[1]);
		}
	}

	const hexColorGradient = (context: CanvasRenderingContext2D, color: string) => {
		const [red, green, blue] = [
			color.substring(1, 3),
			color.substring(3, 5),
			color.substring(5, 7),
		].map((s) => parseInt(s, 16));

		const gradient = context.createLinearGradient(0, 0, 0, props.height ?? 200);
		gradient.addColorStop(0, `rgba(${red}, ${green}, ${blue}, 0.4)`);
		gradient.addColorStop(1, `rgba(${red}, ${green}, ${blue}, 0.0)`);

		return gradient;
	};

	const data = () => {
		return {
			labels: props.labels,
			datasets: [
				{
					data: props.data,
					fill: 'start',
					backgroundColor: (context: ScriptableContext<'line'>) =>
						hexColorGradient(context.chart.ctx, props.tintColor ?? theme.colors.purple),
					borderColor: props.tintColor ?? theme.colors.purple,
					pointBackgroundColor: 'transparent',
					pointBorderColor: 'transparent',
					pointRadius: 20,
					pointHoverRadius: 20,
					pointStyle: false,
				},
			],
		};
	};

	const options = {
		maintainAspectRatio: false,
		responsive: true,
		layout: {
			padding: 0,
			margin: 0,
		},
		scales: {
			x: {
				display: !(props.compactLayout || props.minimalLayout),
				grid: {
					display: false,
				},
				ticks: {
					display: !props.compactLayout,
					color: theme.colors.slateBlue,
					font: {
						weight: 'bold',
						size: 12,
						family: 'Source Sans Pro',
					},
				},
			},
			y: {
				display: !props.minimalLayout,
				beginAtZero: true,
				min,
				max,
				grid: {
					display: true,
					color: theme.colors.lightBlue,
				},
				ticks: {
					precision: 0,
					mirror: !!props.compactLayout,
					callback: props.yFormat,
				},
			},
		},
		elements: {
			line: {
				tension: 0.35,
			},
		},
		plugins: {
			filler: {
				propagate: false,
			},
			legend: {
				display: false,
			},
			tooltip: {
				enabled: true,
				callbacks: {
					label: function (context: any) {
						const source = context.dataset.data[context.dataIndex];

						if (source?.tooltip) {
							return source?.tooltip;
						}

						return props.yFormat ? props.yFormat(context.parsed.y) : context.parsed.y.toFixed(1);
					},
				},
			},
		},
		interaction: {
			intersect: true,
		},
	};

	const lineContents = (
		<div style={{ position: 'relative', zIndex: 2 }}>
			<Line data={data() as any} options={options} height={props.height} />
		</div>
	);

	return (
		<div style={{ margin: '-10px -20px -20px -20px' }}>
			{props.showBackground ? (
				<LineChartBackground>{lineContents}</LineChartBackground>
			) : (
				lineContents
			)}
		</div>
	);
}

const AnalyticsGraphMetricEmpty = styled(Flex)`
	height: 120px;
	background-color: ${(props) => props.theme.colors.whiteBlue};
	color: ${(props) => props.theme.colors.slateBlue};
	align-items: center;
	justify-content: center;
`;

export function AnalyticsGraphMetric(props: {
	metricGroupID: string;
	metric: graphql.AnalyticsGraphMetric;
}) {
	const { t } = useTranslation();

	if (
		props.metric.datapoints.length < 2 ||
		props.metric.datapoints.every((datapoint) => datapoint.value === 0)
	) {
		return (
			<AnalyticsGraphMetricEmpty>{t('analytics.metrics.graph.empty')}</AnalyticsGraphMetricEmpty>
		);
	}

	return (
		<LineChartGradient
			data={props.metric.datapoints.map((datapoint) => datapoint.value) as any}
			labels={props.metric.datapoints.map((datapoint) => moment(datapoint.timestamp).format('LL'))}
			bounds={
				typeof props.metric.min === 'number' && typeof props.metric.max === 'number'
					? [props.metric.min, props.metric.max]
					: undefined
			}
			compactLayout
			showBackground
		/>
	);
}

AnalyticsGraphMetric.Skeleton = styled(Skeleton.Box)`
	height: 120px;
	width: 100%;
`;
